/** Ukrainian plural form: 1 авто, 2 авто, 5 авто (pass the three forms). */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Ukrainian UI strings (D22: one object, no i18n library). */
export const uk = {
  appName: 'ТЕЛЕГА',
  tabs: { fleet: 'Автопарк', channels: 'Канали', template: 'Шаблон' },
  common: {
    retry: 'Спробувати ще раз',
    save: 'Зберегти',
    cancel: 'Скасувати',
    next: 'Далі',
    close: 'Закрити',
    loading: 'Завантаження…',
    error: 'Щось пішло не так',
    offline: 'Немає зв’язку з сервером. Перевірте інтернет і спробуйте ще раз.',
    fixFields: 'Перевірте виділені поля',
  },
  auth: {
    phoneSaved: 'Телефон збережено',
    outsideTitle: 'Привіт! Це ТЕЛЕГА',
    outsideText:
      'Бот для продавців авто. Опишіть авто один раз, і воно з’явиться в усіх ваших Telegram-каналах, а ціна й статус оновлюватимуться самі. Працює всередині Telegram: відкрийте бота і натисніть «Автопарк».',
    outsideButton: 'Відкрити бота в Telegram',
    failedTitle: 'Не вдалося увійти',
    expiredText: 'Сесія закінчилась. Закрийте й відкрийте застосунок знову.',
  },
  fleet: {
    emptyTitle: 'Поки що жодного авто',
    emptyText: 'Додайте перше авто — це займе 2 хвилини.',
    add: 'Додати авто',
    search: 'Пошук: марка, модель або VIN',
    all: 'Усі',
    nothingFound: 'Нічого не знайдено',
    nothingFoundText: 'Спробуйте інший запит або фільтр.',
    inWork: (n: number) => `${n} ${plural(n, 'авто', 'авто', 'авто')} в роботі`,
    currency: 'Валюта',
    invested: 'Вкладено',
    margin: 'Маржа',
    totalsHint: 'Маржа рахується лише по авто з витратами, без конвертації валют.',
    noChannelsTitle: 'Підключіть канал',
    noChannelsText: 'Щоб публікувати авто, додайте бота адміністратором у свій Telegram-канал.',
    open: 'Відкрити',
  },
  channels: {
    title: 'Канали',
    telegramHeader: 'Telegram',
    manualHeader: 'Вручну',
    manualFooter:
      'Для цих майданчиків ТЕЛЕГА готує текст і фото, а викладаєте ви. Коли зміниться ціна чи статус, бот нагадає оновити.',
    emptyTitle: 'Ще немає каналів',
    emptyText: 'Додайте бота адміністратором у свій канал або групу — і він з’явиться тут.',
    addBot: 'Додати бота в канал',
    addAgain: 'Додати знову',
    kindChannel: 'Канал',
    kindGroup: 'Група',
    canPublish: 'можна публікувати',
    noRights: 'боту бракує прав',
    disconnected: 'Відключено',
    pin: 'Закріп «Наявність»',
    pinFooter: 'Один закріплений пост зі списком авто в наявності. Оновлюється сам.',
    pinNoRightChannel: 'Щоб закріплювати, дайте боту право редагувати повідомлення.',
    pinNoRightGroup: 'Щоб закріплювати, дайте боту право закріплювати повідомлення.',
    cars: (n: number) => `${n} авто`,
    addManual: (name: string) => `Додати ${name}`,
    removeConfirm: (name: string) =>
      `Прибрати ${name} зі списку? Позначки «Викладено» для цього майданчика зникнуть.`,
    sheetTitle: 'Як підключити канал',
    steps: [
      'Натисніть кнопку нижче й оберіть свій канал або групу.',
      'Підтвердіть права: публікація та редагування повідомлень.',
      'Готово — бот напише вам, що канал підключено.',
    ],
    openChannelPicker: 'Обрати канал',
    openGroupPicker: 'Обрати групу',
    noBotUsername: 'Ім’я бота не налаштоване (VITE_BOT_USERNAME).',
  },
  template: {
    title: 'Шаблон',
    contacts: 'Контакти',
    phone: 'Телефон',
    telegram: 'Telegram',
    conditions: 'Умови',
    conditionsPlaceholder: 'Торг біля авто. Допомагаємо з оформленням.',
    hashtags: 'Хештеги',
    hashtagsFooter: 'До 10 хештегів. Модель авто додається автоматично.',
    emoji: 'Емодзі в постах',
    length: 'Довжина тексту за замовчуванням',
    preview: 'Прев’ю',
    previewFooter: 'Так виглядатиме пост. Шаблон застосовується до всіх ваших постів.',
    saved: 'Шаблон збережено',
  },
  car: {
    title: 'Авто',
    notFound: 'Авто не знайдено',
    preview: 'Прев’ю посту в Telegram',
    editData: 'Редагувати дані',
    editText: 'Текст посту',
    priceHeader: 'Ціна',
    change: 'Змінити',
    newPrice: 'Нова ціна',
    updatesPosts: (n: number) =>
      n === 0
        ? 'Постів ще немає — ціна з’явиться в нових публікаціях.'
        : `Оновить ${n} ${plural(n, 'пост', 'пости', 'постів')} і закріп «Наявність».`,
    priceSaved: (n: number) =>
      n === 0 ? 'Ціну змінено' : `Ціну оновлено в ${n} ${plural(n, 'пості', 'постах', 'постах')}`,
    reduced: 'ціну знижено',
    archive: 'В архів',
    archiveConfirm: (wasSold: boolean) =>
      wasSold
        ? 'Перенести авто в архів? Воно зникне зі списку, а пости в каналах лишаться «ПРОДАНО».'
        : 'Перенести авто в архів? Воно зникне зі списку, закріпу «Наявність» та inline, а пости в каналах позначаться «НЕДОСТУПНО».',
    archived: 'Авто в архіві',
    soldFor: (amount: string) => `Продано за ${amount}`,
    editTitle: 'Дані авто',
    saved: 'Збережено',
    textReset: 'Опис оновлено під нові дані авто',
    templateFailed: 'Не вдалося завантажити шаблон посту.',
    statusHeader: 'Статус',
    statusFooter:
      'Розмитнення й наявність змінюються одразу. Для інших статусів спершу заповніть форму.',
    statusChanged: (n: number) =>
      n === 0
        ? 'Статус змінено'
        : `Статус змінено, оновлюємо ${n} ${plural(n, 'пост', 'пости', 'постів')}`,
    etaLine: (eta: string) => `Прибуття ${eta}`,
    reserveTitle: 'Бронь',
    reserveNote: 'Коментар до броні',
    reservePlaceholder: 'Хто, телефон, завдаток, до якої дати',
    reserveFooter: 'Бачите тільки ви. У пости, закріп і inline це не потрапляє.',
    transitTitle: 'В дорозі',
    transitFooter: 'У пості буде «в дорозі, прибуття ~дата», у закріпі — «(~дата)».',
    soldTitle: 'Продано',
    soldPrice: 'Сума продажу',
    soldCurrency: 'Валюта',
    soldFooter:
      'У всіх постах з’явиться «✅ ПРОДАНО» без ціни й контактів, авто зникне із закріпу.',
    soldNote: 'Коментар',
    soldNotePlaceholder: 'Необов’язково: кому продали, як розрахувались',
    soldNoteFooter: 'Бачите тільки ви. У пости, закріп і inline це не потрапляє.',
    soldLocked: 'Продане авто можна лише перенести в архів.',
    archivedFooter: 'Авто в архіві. Оберіть статус, щоб повернути його в роботу.',
    archivedSoldLocked: 'Продане авто з архіву не повертається.',
    whereHeader: 'Де опубліковано',
    whereEmpty: 'Ще ніде. Опублікуйте авто в своїх каналах.',
    whereEmptyClosed: 'Не публікувалось.',
    pubPosted: 'Опубліковано',
    pubQueued: 'Публікується…',
    pubFailed: 'Помилка',
    pubDeleted: 'Пост видалено в Telegram',
    pubManualPending: 'Потрібно викласти або оновити',
    pubManualDone: 'Викладено',
    open: 'Відкрити',
    retry: 'Повторити',
    republish: 'Опублікувати знову',
    publishMore: 'Опублікувати в інших каналах',
    publishFirst: 'Опублікувати в каналах',
    repost: 'Опублікувати заново',
    repostHint: 'Новий пост з’явиться внизу каналу.',
    repostPriceFooter: 'Нова ціна оновить і попередні пости.',
    repostWhere: 'Куди',
    repostWhereFooter: 'Попередні пости лишаться в каналах і теж оновлюватимуться.',
    rewriteText: 'Переписати текст',
    reposted: (n: number) => `Публікуємо заново в ${n} ${plural(n, 'канал', 'канали', 'каналів')}`,
    publish: 'Опублікувати',
    copyText: 'Скопіювати текст',
    copied: 'Текст скопійовано',
    savePhotos: 'Зберегти фото',
    savePhotosHint: 'Натисніть на фото, щоб зберегти його.',
    markDone: 'Позначити «Викладено»',
    manualHint:
      'Скопіюйте текст і збережіть фото, викладіть оголошення, потім позначте «Викладено». Коли зміниться ціна чи статус, бот нагадає оновити.',
    retried: 'Пробуємо ще раз',
    noteHeader: 'Нотатка · бачите тільки ви',
    noteAdd: 'Додати нотатку',
    noteTitle: 'Нотатка',
    noteLabel: 'Нотатка про авто',
    notePlaceholder: 'Що варто пам’ятати про це авто',
    noteFooter: 'Бачите тільки ви. У пости, закріп, inline і списки це не потрапляє.',
    noteSaved: 'Нотатку збережено',
    noteRemoved: 'Нотатку видалено',
    costsHeader: 'Витрати і маржа · бачите тільки ви',
    costsFooter:
      'Витрату не можна змінити — лише видалити й додати знову. Суми в різних валютах не конвертуються.',
    addCost: 'Додати витрату',
    newCost: 'Нова витрата',
    costType: 'Тип',
    costAmount: 'Сума',
    costCurrency: 'Валюта',
    add: 'Додати',
    deleteCost: (what: string) =>
      `Видалити витрату «${what}»? Змінити її не можна — лише видалити й додати знову.`,
    costTotal: 'Собівартість',
    margin: 'Маржа',
    marginMixed: 'Витрати в різних валютах — маржу рахуєте самі.',
    costAmountError: 'Сума цілим числом, більше нуля',
    textHeader: 'Текст посту',
    regenerate: 'Написати заново',
    textSaved: (n: number) =>
      n === 0
        ? 'Текст збережено'
        : `Текст оновлено в ${n} ${plural(n, 'пості', 'постах', 'постах')}`,
    photosHeader: 'Фото',
    managePhotos: (n: number) => `Фото: ${n}`,
    photosTitle: 'Фото авто',
    photosFooter: 'Перше фото — головне, воно ж перше в пості. Нове головне фото оновить пости.',
    photosSaved: 'Фото збережено',
    deletePhotoConfirm: 'Видалити це фото?',
  },
  newCar: {
    title: 'Нове авто',
    back: 'Назад',
    viewPhoto: 'Переглянути',
    steps: ['Фото', 'Дані', 'Текст', 'Куди'],
    stepOf: (n: number, total: number, name: string) => `Крок ${n} з ${total} · ${name}`,
    addPhotos: 'Додати фото',
    photosFooter: 'У пост підуть перші 10 фото. Мінімум 2 для публікації.',
    photoCount: (n: number) => `${n} ${plural(n, 'фото', 'фото', 'фото')}`,
    makeMain: 'Зробити головним',
    removePhoto: 'Видалити фото',
    retryUpload: 'Завантажити ще раз',
    mainBadge: 'Головне',
    photoActions: 'Фото',
    vin: 'VIN',
    decode: 'Розпізнати',
    vinFooter: 'Для авто з США заповнимо марку, модель, рік і техніку. Для інших VIN — лише марку.',
    vinFilled: (fields: string) => `Заповнено: ${fields}`,
    vinMakeOnly: 'Розпізнали лише марку — решту заповніть вручну.',
    vinNothing: 'Не вдалося розпізнати — заповніть поля вручну.',
    vinKept: 'VIN розпізнано, поля вже заповнені — нічого не змінювали.',
    scan: 'Сканувати техпаспорт',
    scanFooter:
      'Наведіть камеру на техпаспорт або на екран Дії — заповнимо марку, модель, рік, VIN, двигун, паливо і колір. Фото ніде не зберігаємо.',
    scanTitle: 'Техпаспорт',
    scanStep: (n: number, of: number) => `Крок ${n} з ${of}`,
    scanSides: {
      car: {
        title: 'Бік з маркою і VIN',
        intro: 'Наведіть камеру на цей бік і натисніть «Сканувати»',
        note: 'Або наведіть на техпаспорт у Дії — там усе на одному екрані.',
      },
      owner: {
        title: 'Бік з власником',
        intro: 'Переверніть картку і натисніть «Сканувати»',
        note: 'Звідси беремо лише рік випуску. Ім’я та адресу власника не читаємо.',
      },
    },
    scanFields: {
      make: 'Марка',
      model: 'Модель',
      year: 'Рік випуску',
      vin: 'VIN',
      engine: 'Двигун',
      fuel: 'Паливо',
      color: 'Колір',
    },
    scanStarting: 'Вмикаю камеру…',
    scanReading: 'Читаю… це кілька секунд',
    scanMissing: (fields: string) =>
      `Не прочитав ${fields}. Наблизьте камеру й приберіть відблиски.`,
    scanOtherSide: 'Це інший бік — переверніть картку.',
    scanNotFound: 'Не бачу техпаспорта. Наблизьте камеру й приберіть відблиски.',
    scanShoot: 'Сканувати',
    scanAgain: 'Сканувати ще раз',
    scanBusy: 'Читаю…',
    scanNext: 'Далі',
    scanSkip: 'Пропустити',
    scanOpenCamera: 'Відкрити камеру',
    scanNoCamera: 'Камера всередині Telegram недоступна — сфотографуйте цей бік системною камерою.',
    scanDoneTitle: 'Зчитано з техпаспорта',
    scanNotRead: 'не прочитано',
    scanDoneFooter: 'Заповнимо лише порожні поля — усе можна виправити у формі.',
    scanFilled: (fields: string) => `Заповнено з техпаспорта: ${fields}`,
    scanNothing: 'Нових даних немає: поля вже заповнені або не прочиталися.',
    scanDone: 'Готово',
    scanLimit: 'Ліміт сканувань на сьогодні вичерпано. Завтра можна знову.',
    scanUnavailable: 'Сканування техпаспорта зараз недоступне.',
    carHeader: 'Авто',
    saleHeader: 'Продаж',
    make: 'Марка',
    model: 'Модель',
    year: 'Рік',
    mileage: 'Пробіг',
    engine: 'Двигун',
    enginePlaceholder: '2.0 TSI',
    fuel: 'Паливо',
    gearbox: 'Коробка',
    drive: 'Привід',
    color: 'Колір',
    origin: 'Звідки',
    price: 'Ціна',
    priceFooter: 'Ціна в доларах, під ключ.',
    city: 'Місто',
    status: 'Статус',
    eta: 'Дата прибуття',
    statusLocked: 'Статус далі змінюється в картці авто.',
    required: 'Обов’язкове поле',
    leaveConfirm: 'Вийти? Незбережені дані буде втрачено.',
    uploading: (done: number, total: number) => `Фото: ${done} з ${total}`,
    textLength: 'Довжина',
    description: 'Опис',
    descriptionPlaceholder: 'Опис авто',
    textFailed: 'Не вдалося написати текст. Напишіть свій або спробуйте ще раз.',
    aiSource: 'Текст написав ШІ — можна редагувати.',
    templateSource: 'Текст за шаблоном — можна редагувати.',
    textFooter: 'Ціну, статус, контакти й хештеги ми додамо самі й оновлюватимемо автоматично.',
    counter: (length: number, limit: number) => `${length} / ${limit}`,
    tooLong: (limit: number) => `Опис задовгий: максимум ${limit} символів.`,
    preview: 'Прев’ю посту',
    whereTelegram: 'Telegram',
    whereManual: 'Вручну',
    manualFooter: 'Для цих майданчиків підготуємо текст і фото, а викладете ви.',
    noTelegram: 'Немає підключених каналів. Додайте бота у свій канал на вкладці «Канали».',
    cannotPublish: 'бот не може публікувати',
    publish: 'Опублікувати',
    saveWithoutPublishing: 'Зберегти без публікації',
    waitingPhotos: (done: number, total: number) => `Завантажуємо фото ${done}/${total}…`,
    pickChannel: 'Оберіть хоча б один канал.',
    needPhotos: 'Для Telegram потрібно щонайменше 2 фото.',
    failedPhotos: 'Частина фото не завантажилась — повторіть або видаліть їх на кроці «Фото».',
    published: (n: number) => `Публікуємо в ${n} ${plural(n, 'канал', 'канали', 'каналів')}`,
  },
} as const;
